/**
 * Projects API
 * HTTP calls for projects, project-level git settings, and project members
 */

import apiClient, { TokenManager } from '../../../../shared/lib/apiClient';
import type { Project, ProjectGitSettings } from '../types';

export interface ProjectMember {
  user_id: string;
  permission: 'owner' | 'read' | 'readwrite';
  name?: string;
  /** Present when backend resolved it (userprofile row and/or Cognito). */
  email?: string;
}

export interface ProjectMembersResponse {
  members: ProjectMember[];
}

/**
 * Get a single project by ID (includes git_settings when present).
 */
export async function getProject(projectId: string | number): Promise<Project> {
  const id = String(projectId);
  return apiClient<Project>(`/projects/${id}`, {
    method: 'GET',
    requireAuth: true
  });
}

export type GetProjectMembersOptions = {
  /**
   * When true, returns only collaborators with read/readwrite permissions (excludes project owner)
   * and includes email when the backend can resolve it (userprofile + Cognito fallback).
   */
  forTagging?: boolean;
};

/**
 * List users with access to the project, or collaborators-only for @mention tagging.
 * Passes user_id query param (same idea as userprojectpermissions GET); still sends Bearer
 * token if API Gateway requires it.
 */
export async function getProjectMembers(
  projectId: string | number,
  options?: GetProjectMembersOptions
): Promise<ProjectMembersResponse> {
  const id = String(projectId);
  const userId = TokenManager.getUserId();
  if (!userId) {
    throw new Error('User ID is required to load project members');
  }
  const params = new URLSearchParams({ user_id: userId });
  if (options?.forTagging) {
    params.set('for_tagging', 'true');
  }
  return apiClient<ProjectMembersResponse>(`/projects/${id}/members?${params.toString()}`, {
    method: 'GET',
    requireAuth: true,
    // Members fetch is optional; 401 here must not clear session / redirect to login (e.g. route not deployed yet).
    redirectToLoginOn401: false,
  });
}

/**
 * Remove shared access for a user (project owner only). Returns 204 from the API.
 */
export async function revokeProjectMember(
  projectId: string | number,
  memberUserId: string
): Promise<void> {
  const id = String(projectId);
  const userId = TokenManager.getUserId();
  if (!userId) {
    throw new Error('User ID is required to revoke access');
  }
  const enc = encodeURIComponent(memberUserId);
  const params = new URLSearchParams({ user_id: userId });
  await apiClient(`/projects/${id}/members/${enc}?${params.toString()}`, {
    method: 'DELETE',
    requireAuth: true,
    redirectToLoginOn401: false,
  });
}

/**
 * Update project-level git settings. Persists to DynamoDB.
 */
export async function updateProjectGitSettings(
  projectId: string | number,
  gitSettings: ProjectGitSettings
): Promise<Project> {
  const id = String(projectId);
  const userId = TokenManager.getUserId();
  if (!userId) {
    throw new Error('User ID is required to update project settings');
  }
  const params = new URLSearchParams({ user_id: userId });
  return apiClient<Project>(`/projects/${id}?${params.toString()}`, {
    method: 'PUT',
    body: { git_settings: gitSettings },
    requireAuth: true
  });
}
